import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import { Loader2 } from "lucide-react";
import remarkGfm from "remark-gfm";

import { isVideoSource } from "../markdown";
import { isUploadingSource, uploadingFileName } from "../media/media-placeholder";
import remarkUnderline from "../remark-underline";

const urlTransform = (url: string) => (isUploadingSource(url) ? url : defaultUrlTransform(url));

const Media = ({ src, alt, onError }: { src?: string; alt?: string; onError?: () => void }) => {
  if (src && isUploadingSource(src)) {
    return (
      <span className="my-4 flex items-center gap-3 rounded-xl border border-dashed border-[#B5D66A] bg-[#F7FBEA] px-4 py-5">
        <Loader2 size={20} className="shrink-0 animate-spin text-[#99C32C]" />
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-ui-primary">Enviando…</span>
          <span className="block truncate text-xs text-ui-text-muted">{uploadingFileName(src)}</span>
        </span>
      </span>
    );
  }

  if (src && isVideoSource(src)) {
    return <video src={src} controls onError={onError} className="my-4 w-full rounded-xl bg-black" />;
  }

  return <img src={src} alt={alt ?? ""} onError={onError} className="my-4 max-h-96 w-full rounded-xl object-contain" />;
};

interface MarkdownPreviewProps {
  markdown: string;
  onMediaError?: () => void;
}

const MarkdownPreview = ({ markdown, onMediaError }: MarkdownPreviewProps) => {
  if (!markdown.trim()) {
    return <p className="text-sm text-ui-text-muted">Nada para visualizar ainda.</p>;
  }

  return (
    <div className="space-y-3 text-sm leading-6 text-ui-primary">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkUnderline]}
        urlTransform={urlTransform}
        components={{
          h2: ({ children }) => <h2 className="pt-2 text-xl font-semibold text-ui-primary">{children}</h2>,
          h3: ({ children }) => <h3 className="pt-1 text-base font-semibold text-ui-primary">{children}</h3>,
          p: ({ children }) => <p>{children}</p>,
          ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
          blockquote: ({ children }) => (
            <blockquote className="rounded-xl border-l-4 border-lime-400 bg-[#F7FBEA] px-4 py-3 text-ui-primary">{children}</blockquote>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto rounded-xl border border-ui-border">
              <table className="w-full border-collapse text-left">{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="bg-gray-50 px-3 py-2 font-semibold">{children}</th>,
          td: ({ children }) => <td className="border-t border-gray-100 px-3 py-2">{children}</td>,
          img: ({ src, alt }) => <Media src={src} alt={alt} onError={onMediaError} />,
          a: ({ href, children }) => (
            <a href={href} className="text-brand-primary underline" target="_blank" rel="noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownPreview;
